import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import classes from '../appForm/style.module.css';
import Navbar from '../../navbar';
import Header from '../../header';
import { fetchApplication, updateApplicationData, safePdf, getApplication, getFormMandatoryFields, isThunkFulfilled } from '../../../store/reducers/applicationSlice'
import { getSignature, getAttachments } from '../../../store/reducers/fileSlice';
import { setFormFieldData, hideAllPagesExceptCurrentFormPage, prepareHtmlForPdf } from '../../../utility/utility';
import FormHeader from '../forms/formHeader/formHeader';
import AddAttachment from './attachments';
import FormLoader from '../forms/formLoader';
import { useNavigate } from "react-router-dom";
import { showSuccess, showError } from '../../../helpers/toast';
import { createNotification } from '../../../store/reducers/notificationSlice';

const Draft = () => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const authToken = user.access_token;
  const params = useParams();
  // const application = useSelector((state) => state.applications.application)
  const [application, setApplicationData] = useState();
  const [pageIds, setPageIds] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [formIds, setFormIds] = useState([]);
  const [signatureImage, setSignatureImage] = useState('')
  const [inputFields, setInputFields] = useState({});
  const [signatureIds, setSignatureIds] = useState([]);
  const [isLoaded, setIsLoaded] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [imageData, setImageData] = useState('');
  const [showAttachments, setShowAttachments] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    const fetchApplication = async () => {
      if (params.id && authToken) {
        const data = await getApplication(params.id, authToken);
        let app = data;
        if (data && typeof data === "object" && data.json_data === undefined && !data.name) {
          if (data.application) {
            app = data.application;
          } else if (data.data) {
            app = data.data;
          } else if (Array.isArray(data.applications) && data.applications[0]) {
            app = data.applications[0];
          } else if (data.applications && !Array.isArray(data.applications)) {
            app = data.applications;
          }
        }
        setApplicationData(app);
      }
    };
    fetchApplication();
  }, [params.id, authToken]);

  // Company admins and employees can only open their own drafts
  useEffect(() => {
    if (!application || !user?.user) return;

    const role = user.user.user_role;
    if (role !== "company_admin" && role !== "employee") return;

    const ownerId = application.user_id || application.userId;
    if (ownerId != null && String(ownerId) !== String(user.user.id)) {
      showError("You do not have permission to view this draft");
      navigate("/draft");
    }
  }, [application, user, navigate]);

  useEffect(() => {
    const runAsyncFunctions = async () => {
      if (isLoaded && user && application && formIds.length > 0) {
        if (application.json_data) {
          await setFormFieldData(application.json_data);
        }

        let jsonFields = {};
        try {
          if (
            typeof application.json_data === "string" &&
            application.json_data.trim().startsWith("{")
          ) {
            jsonFields = JSON.parse(application.json_data);
          } else if (
            typeof application.json_data === "object" &&
            application.json_data !== null
          ) {
            jsonFields = application.json_data;
          } else {
            // Handle cases like [object Object]
            jsonFields = {};
          }
        } catch (e) {
          // Fallback if parsing fails
          jsonFields = {};
        }
        setInputFields(jsonFields);
        // Always load files for the application owner (employee), not the logged-in company admin
        const ownerUserId = application.user_id || application.userId;

        // Use Promise.allSettled to handle both API calls gracefully
        const [signatureResult, attachmentsResult] = await Promise.allSettled([
          getSignature({ userId: ownerUserId, applicationId: params.id, authToken }),
          getAttachments({ userId: ownerUserId, applicationId: params.id, authToken })
        ]);

        // Handle signature result
        if (signatureResult.status === 'fulfilled' && signatureResult.value) {
          setSignatureImage(signatureResult.value);
        } else {
          const error = signatureResult.reason;
          if (error && error.message === 'NOT_FOUND') {
          } else if (error && error.message === 'SERVER_ERROR') {
          } else {
          }
        }

        // Handle attachments result
        if (attachmentsResult.status === 'fulfilled' && attachmentsResult.value) {
          const files = attachmentsResult.value.map(filename => ({ name: filename }));
          setUploadedFiles(files);
        } else {
          const error = attachmentsResult.reason;
          if (error && error.message === 'NOT_FOUND') {
          } else if (error && error.message === 'SERVER_ERROR') {
          } else {
          }
          setUploadedFiles([]);
        }
      }
    };

    runAsyncFunctions();
  }, [isLoaded, user, application, formIds.length, params.id, authToken]);

  useEffect(() => {
    if (signatureImage != '' && signatureIds.length > 0) {
      signatureIds.forEach(id => {
        const imageInput = document.getElementById(id);
        if (imageInput && imageInput.type === 'image') {
          // Directly set the src of the image input
          // let originalWidth = window.getComputedStyle(imageInput).width.replace("px", "")
          // let originalTop = window.getComputedStyle(imageInput).top.replace("px", "")
          // let height = (originalWidth * 114) / 179;
          // imageInput.style.height = height + "px";
          // imageInput.style.top = (originalTop - height) + "px";
          imageInput.src = signatureImage;
          setImageData(signatureImage);
        }
      });
    }
  }, [signatureImage])

  useEffect(() => {
    if (pageIds) {
      hideAllPagesExceptCurrentFormPage(pageIds, currentPage);
    }
  }, [pageIds])

  useEffect(() => {
    if (formIds.length > 0) {
      attachEventHandlers();
    }
  }, [formIds.length]);

  const attachEventHandlers = () => {
    let tempSignatureIds = []

    formIds.forEach(id => {
      const inputField = document.getElementById(id);
      if (inputField.type === 'image') {
        tempSignatureIds.push(id);

      } else if (inputField.type === 'checkbox') {
        inputField.addEventListener('change', handleCheckBoxChange)
      } else {
        inputField.addEventListener('change', handleInputChange);
      }
    });
    setSignatureIds(tempSignatureIds);
  };
  const handleInputChange = (event) => {
    const { id, value } = event.target;

    setInputFields(prevInputFields => ({
      ...prevInputFields,
      [id]: value,
    }));
  };
  const handleCheckBoxChange = (event) => {
    const { id, checked, value } = event.target;

    setInputFields(prevInputFields => {
      // If the checkbox is checked, add it to inputFields
      if (checked) {
        return {
          ...prevInputFields,
          [id]: value,
        };
      } else {
        // If the checkbox is unchecked, remove it from inputFields
        const { [id]: removedValue, ...remainingFields } = prevInputFields;
        return remainingFields;
      }
    })
  };
  const previousPage = () => {
    if (currentPage == pageIds.length + 1) {
      setShowAttachments(false);
      showDiv(currentPage - 1);
      setCurrentPage(currentPage - 1)
    } else {
      if (currentPage - 1 >= 1) {
        hideDiv(currentPage);
        showDiv(currentPage - 1);
        setCurrentPage(currentPage - 1);
      }
    }
  }

  const nextPage = () => {
    if (currentPage + 1 <= pageIds.length) {
      hideDiv(currentPage);
      showDiv(currentPage + 1);
      setCurrentPage(currentPage + 1);
    } else if (currentPage + 1 <= pageIds.length + 1) {
      hideDiv(currentPage);
      setCurrentPage(currentPage + 1);
      setShowAttachments(true);
    }
  }
  const hideDiv = (pageNo) => {
    const pageDiv = document.getElementById("page" + pageNo);
    pageDiv.style.display = "none";
  }

  const showDiv = (pageNo) => {
    const pageDiv = document.getElementById("page" + pageNo);
    pageDiv.style.display = "";
  }

  const handleSubmitWithLoading = async (e) => {
    const mandatory_fields = await fetchApplicationMandatoryFields();
    let isValid = true;
    if (mandatory_fields) {
      isValid = areAllMandatoryFieldsFilled(inputFields, mandatory_fields);
    }
    if (!isValid) {
      showError("Please fill all mandatory fields.");
    } else {
      let htmlForm = document.getElementById("DocumentPDF").outerHTML;

      const isCompanyAdmin = user.user?.user_role === 'company_admin';
      const nextState = isCompanyAdmin
        ? 'in_process'
        : user.user?.user_role === 'employee'
          ? 'application_submitted_by_company_user_to_company'
          : 'in_process';

      const response = await dispatch(updateApplicationData({
        token: authToken,
        id: params.id,
        json_data: inputFields,
        state: nextState,
        userId: application.user_id || application.userId,
        files: uploadedFiles,
        form: application.name

      }));

      if (isThunkFulfilled(response)) {
        try {
          await dispatch(safePdf({
            htmlForm: prepareHtmlForPdf(htmlForm, inputFields, pageIds),
            user: { id: application.user_id },
            id: params.id,
            token: authToken,
          }));
        } catch (pdfError) {
          console.warn('PDF save failed, but draft was saved:', pdfError);
        }

        // Send submission notifications (same logic as initial submit)
        if (user.user?.user_role === 'employee') {
          await dispatch(createNotification({
            user_id: user.user.id,
            applicationId: params.id,
            applicationName: application.name,
            status: 'submitted',
            accessToken: authToken,
            sendToCompanyAdmins: true,
            companyName: user.user?.company_name,
            employeeName: user.user?.full_name
          }));
          showSuccess('Application submitted successfully');
        } else {
          await dispatch(createNotification({
            user_id: user.user.id,
            applicationId: params.id,
            applicationName: application.name,
            status: 'company_submitted',
            accessToken: authToken,
            sendToCompanyAdmins: true,
            sendToGamingBoardAdmins: true,
            sendToGamingBoardReviewers: true,
            sendToGamingBoardViewers: true,
            companyName: user.user?.company_name,
            employeeName: user.user?.full_name
          }));
          showSuccess('Application submitted successfully!');
        }

        if (user.user?.user_role === 'company_admin') {
          navigate('/in-process-applications');
        } else {
          navigate('/in-review');
        }
      }
    }
  };

  const saveToDrafts = async (e) => {
    try {
      let htmlForm = document.getElementById("DocumentPDF")?.outerHTML;
      if (htmlForm) {
      }

      const response = await dispatch(updateApplicationData({
        token: authToken,
        id: params.id,
        json_data: inputFields,
        state: e,
        userId: application.user_id || application.userId,
        files: uploadedFiles,
        form: application.name
      }))

      if (isThunkFulfilled(response)) {
        try {
          await dispatch(safePdf({
            htmlForm: prepareHtmlForPdf(htmlForm, inputFields, pageIds),
            user: { id: application.user_id },
            id: params.id,
            token: authToken,
          }));
        } catch (pdfError) {
          console.warn('PDF save failed, but draft was saved:', pdfError);
        }
        showSuccess('Application saved as draft successfully!');
      } else {
        showError('Failed to save application. Please try again.');
      }
    } catch (error) {
      console.error('Error saving draft:', error);
      showError('Failed to save draft');
    }
  }
  const fetchApplicationMandatoryFields = async () => {
    const data = await getFormMandatoryFields(application.name, authToken);
    if (data) {
      return data[0]?.mandatory_fields;
    }
  };
  const areAllMandatoryFieldsFilled = (inputFields, mandatoryFields) => {
    return mandatoryFields.every((fieldId) => {
      const value = inputFields[fieldId];
      return value !== undefined && value !== null && value !== '';
    });
  };
  const handlePageChange = (number) => {
    if (number > 0 && number <= pageIds.length + 1) {
      if (number == pageIds.length + 1) {
        hideDiv(currentPage);
        setCurrentPage(number);
        setShowAttachments(true);
      } else {
        if (currentPage == pageIds.length + 1) {
          setShowAttachments(false)
          showDiv(number)
          setCurrentPage(number)
        } else {
          hideDiv(currentPage);
          showDiv(number);
          setCurrentPage(number);
        }
      }
    }
  }

  const ownerId = application?.user_id || application?.userId;
  const isRestrictedRole = user.user?.user_role === "company_admin" || user.user?.user_role === "employee";
  const isOwnDraft = ownerId == null || String(ownerId) === String(user.user?.id);

  if (!application || (isRestrictedRole && !isOwnDraft)) {
    return (
      <div className="mainApp">
        <Navbar />
        <Header />
        <div className="wrapper">
          <div className="boxAppBoard boxWhite">
            <div className="text-center p-4">
              <div className="spinner"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Navbar />
      <Header />
      <div className='wrapper'>
        <div className='boxAppBoard boxWhite'>
          <div className="stickyFormHeader">
            <FormHeader applicationName={application?.name} nextPage={nextPage} previousPage={previousPage}
              currentPage={currentPage} totalPages={pageIds.length + 1} handleSubmitWithLoading={handleSubmitWithLoading}
              saveToDrafts={saveToDrafts} handlePageChange={handlePageChange} />
          </div>

          <div className={classes['board-content']}>
            {application && <FormLoader applicationName={application?.name} setFormIds={setFormIds} setPageIds={setPageIds} setIsLoaded={setIsLoaded} />}

          </div>

          {showAttachments && (
            <AddAttachment
              applicationId={params.id}
              userId={application?.user_id || application?.userId}
              signatureIds={signatureIds}
              setUploadedFiles={setUploadedFiles}
              uploadedFiles={uploadedFiles}
              applicationState={application?.state}
              applicationName={application?.name}
              jsonData={inputFields}
            />
          )}
        </div>
      </div>
    </div>
  )
}
export default Draft